// orders.api.ts
/**
 * Orders API Client
 * -----------------
 * Provides typed functions for interacting with the backend /orders endpoints.
 *
 * Version: 1.0
 * Source: Orders API specification (SIB project)
 */

import {
  type WorkloadResponse,
  type CorrectOrderRequest,
  type CreateOrderRequest,
  type GetOrderListResponse,
  type RejectOrderRequest,
  type ResubmitOrderRequest,
} from './dto/order.dto'
import type { GetOrderOverviewResponse, OrderOverviewQuery } from './dto/order-overview.dto'
import { API } from '.'
import type { Order, OrderHistory } from '@/common/order.common'

export function createOrder(payload: CreateOrderRequest) {
  const payloadForApi = {
    ...payload,
    buckets: (payload.buckets ?? []).map((b: any) => ({
      // accept both, but prefer the new internal name
      serviceType: b.serviceCode ?? b.serviceType,
      plannedHours: b.plannedHours ?? 0,
    })),
  }

  return API<Order>(
    '/orders',
    {
      method: 'POST',
      body: JSON.stringify(payloadForApi),
    },
    'Fehler beim Erstellen der Bestellung',
    'ORDER_CREATE_ERROR'
  )
}

/**
 * Get list of orders (with optional filters)
 */
export async function getOrders(params?: {
  userId?: string
  bankId?: number
  status?: string
  year?: number | number[]
  page?: number
  pageSize?: number
}) {
  const query = new URLSearchParams()
  if (params?.userId) query.append('userId', params.userId)
  if (params?.bankId) query.append('bankId', String(params.bankId))
  if (params?.status) query.append('status', params.status)
  if (params?.year) {
    const years = Array.isArray(params.year) ? params.year : [params.year]
    years.forEach(y => query.append('year', String(y)))
  }
  if (params?.page) query.append('page', String(params.page))
  if (params?.pageSize) query.append('pageSize', String(params.pageSize))

  return API<GetOrderListResponse>(
    `/orders?${query.toString()}`,
    {},
    'Fehler beim Laden der Bestellungen',
    'ORDERS_FETCH_ERROR'
  )
}

/**
 * Get the Union order overview list: open/approved/rejected orders with
 * server side filtering, sorting, pagination and gross value.
 */
export function getOrderOverview(params: OrderOverviewQuery = {}) {
  const query = new URLSearchParams()
  ;(params.status ?? []).forEach(s => query.append('status', s))
  if (params.search) query.append('search', params.search)
  if (params.year) query.append('year', String(params.year))
  if (params.sibId) query.append('sibId', params.sibId)
  if (params.bankId) query.append('bankId', String(params.bankId))
  if (params.sortBy) query.append('sortBy', params.sortBy)
  if (params.sortDir) query.append('sortDir', params.sortDir)
  if (params.page) query.append('page', String(params.page))
  if (params.pageSize) query.append('pageSize', String(params.pageSize))

  return API<GetOrderOverviewResponse>(
    `/orders/overview?${query.toString()}`,
    {},
    'Fehler beim Laden der Bestellübersicht',
    'ORDER_OVERVIEW_FETCH_ERROR'
  )
}

export function getOrderById(id: number) {
  return API<Order>(`/orders/${id}`, {}, 'Fehler beim Laden der Bestellung', 'ORDER_FETCH_ERROR')
}

export function approveOrder(id: number) {
  return API<Order>(
    `/orders/${id}/approve`,
    { method: 'POST' },
    'Fehler beim Freigeben der Bestellung',
    'ORDER_APPROVE_ERROR'
  )
}

export function rejectOrder(id: number, body: RejectOrderRequest) {
  return API<Order>(
    `/orders/${id}/reject`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    'Fehler beim Ablehnen der Bestellung',
    'ORDER_REJECT_ERROR'
  )
}

export function correctOrder(id: number, body: CorrectOrderRequest) {
  return API<Order>(
    `/orders/${id}/correct`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    'Fehler beim Korrigieren der Bestellung',
    'ORDER_CORRECT_ERROR'
  )
}

export function resubmitOrder(id: number, body: ResubmitOrderRequest) {
  return API<Order>(
    `/orders/${id}/resubmit`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    'Fehler beim Erneuten Einreichen der Bestellung',
    'ORDER_RESUBMIT_ERROR'
  )
}

export function acceptCorrection(id: number) {
  return API<Order>(
    `/orders/${id}/accept-correction`,
    {
      method: 'POST',
    },
    'Fehler beim Akzeptieren der Korrektur',
    'ORDER_ACCEPT_CORRECTION_ERROR'
  )
}

export function deleteOrder(id: number) {
  return API(
    `/orders/${id}`,
    {
      method: 'DELETE',
    },
    'Fehler beim Löschen der Bestellung',
    'ORDER_DELETE_ERROR'
  )
}

export async function completeOrder(id: number) {
  return API<Order>(
    `/orders/${id}/complete`,
    {
      method: 'POST',
    },
    'Fehler beim Abschließen der Bestellung',
    'ORDER_COMPLETE_ERROR'
  )
}

export function getOrderHistory(id: number) {
  return API<OrderHistory[]>(`/orders/${id}/history`)
}

export async function getWorkload(params: {
  userId: string
  from: string
  to: string
  bankId?: number
  customerId?: number
}) {
  const query = new URLSearchParams()
  query.append('userId', params.userId)
  query.append('from', params.from)
  query.append('to', params.to)
  if (params.bankId) query.append('bankId', String(params.bankId))
  if (params.customerId) query.append('customerId', String(params.customerId))
  return API<WorkloadResponse>(
    `/orders/workload?${query.toString()}`,
    {},
    'Fehler beim Laden der Auslastung',
    'WORKLOAD_FETCH_ERROR'
  )
}
